import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { getBacktestSlim } from "../../../services/backtestsApi";
import { collectUniqueBacktestIds } from "./policyVersionReportLinks";

export type PolicyVersionBacktestReportsState = {
  reportIdByBacktestId: Map<string, string | null>;
  loading: boolean;
};

export function usePolicyVersionBacktestReports(
  backtestIds: Array<string | null | undefined>
): PolicyVersionBacktestReportsState {
  const location = useLocation();
  const uniqueBacktestIds = useMemo(() => collectUniqueBacktestIds(backtestIds), [backtestIds]);
  const backtestIdsKey = uniqueBacktestIds.join("|");

  const [reportIdByBacktestId, setReportIdByBacktestId] = useState<Map<string, string | null>>(
    () => new Map()
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (uniqueBacktestIds.length === 0) {
      setReportIdByBacktestId(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void (async () => {
      const entries = await Promise.all(
        uniqueBacktestIds.map(async (backtestId) => {
          try {
            const backtest = await getBacktestSlim(backtestId);
            const reportId =
              typeof backtest.report_id === "string" && backtest.report_id.trim()
                ? backtest.report_id.trim()
                : null;
            return [backtestId, reportId] as const;
          } catch {
            return [backtestId, null] as const;
          }
        })
      );

      if (cancelled) return;
      setReportIdByBacktestId(new Map(entries));
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [backtestIdsKey, location.key]);

  return { reportIdByBacktestId, loading };
}
